import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppBanner } from '@/components/ui';
import WizardShell from '@/components/wizard/WizardShell';
import wizardStyles from '@/styles/wizard.module.css';
import WizardNav from '@/components/wizard/WizardNav';
import VentureDetailsStep, {
  type VentureDetailsErrors,
} from '@/components/wizard/steps/VentureDetailsStep';
import ContactStep, { type ContactErrors } from '@/components/wizard/steps/ContactStep';
import ReviewStep from '@/components/wizard/steps/ReviewStep';
import ConsentStep, {
  EMPTY_CONSENT_CHECKLIST,
  isConsentChecklistComplete,
  type ConsentChecklistState,
} from '@/components/wizard/steps/ConsentStep';
import {
  useWizardSession,
  type WizardAnswers,
} from '@/context/WizardSessionContext';
import { useTranslation } from '@/i18n/useTranslation';
import { JOURNEY_STEP } from '@/lib/journey-steps';
import {
  isNonEmpty,
  isValidEmail,
  isValidPhone,
} from '@/lib/validation/contact';
import {
  trackConsentAccepted,
  trackQuestionnaireStepCompleted,
} from '@/lib/analytics';
import { CONSENT_VERSION, getSessionParcel, patchSessionSteps } from '@/lib/api/sessions';
import { answersToApi } from '@/lib/api/session-mapper';
import { hasWizardParcelSelection } from '@/lib/validation/parcel';
import { submitEntrepreneurIntake } from '@/lib/submit-entrepreneur-intake';
import styles from './WizardScreen.module.css';

const TOTAL_STEPS = 4;

const WIZARD_TO_JOURNEY = [
  JOURNEY_STEP.INTAKE_VENTURE,
  JOURNEY_STEP.INTAKE_CONTACT,
  JOURNEY_STEP.INTAKE_REVIEW,
  JOURNEY_STEP.CONSENT,
] as const;

export type WizardFlow = 'standalone' | 'journey';

type Props = {
  /** Map → questionnaire → assessment. `standalone`: legacy wizard with map. */
  flow?: WizardFlow;
};

function validateVenture(
  answers: WizardAnswers,
  t: ReturnType<typeof useTranslation>,
  options: { requireMap: boolean },
): VentureDetailsErrors {
  const errors: VentureDetailsErrors = {};
  if (!answers.venture_type) {
    errors.venture_type = t.wizard.validation.ventureTypeRequired;
  }
  if (
    answers.required_area_sqm == null ||
    !Number.isFinite(answers.required_area_sqm) ||
    answers.required_area_sqm <= 0
  ) {
    errors.required_area_sqm = t.wizard.validation.requiredAreaRequired;
  }
  if (!answers.timeline) {
    errors.timeline = t.wizard.validation.timelineRequired;
  }
  if (options.requireMap && !hasWizardParcelSelection(answers)) {
    errors.map_location = t.wizard.validation.mapLocationRequired;
  }
  return errors;
}

function validateContact(answers: WizardAnswers, t: ReturnType<typeof useTranslation>): ContactErrors {
  const errors: ContactErrors = {};
  if (!isNonEmpty(answers.contact_name ?? '')) {
    errors.contact_name = t.wizard.validation.contactNameRequired;
  }
  if (!isNonEmpty(answers.contact_phone ?? '') || !isValidPhone(answers.contact_phone ?? '')) {
    errors.contact_phone = t.wizard.validation.contactPhoneInvalid;
  }
  if (!isNonEmpty(answers.contact_email ?? '') || !isValidEmail(answers.contact_email ?? '')) {
    errors.contact_email = t.wizard.validation.contactEmailInvalid;
  }
  return errors;
}

export default function WizardScreen({ flow = 'standalone' }: Props) {
  const isJourney = flow === 'journey';
  const t = useTranslation();
  const navigate = useNavigate();
  const {
    sessionId,
    currentStep,
    answers,
    setAnswers,
    setCurrentStep,
    saveStep,
    saveConsent,
    loading,
    error,
    clearError,
  } = useWizardSession();

  const [ventureErrors, setVentureErrors] = useState<VentureDetailsErrors>({});
  const [contactErrors, setContactErrors] = useState<ContactErrors>({});
  const [consentChecked, setConsentChecked] =
    useState<ConsentChecklistState>(EMPTY_CONSENT_CHECKLIST);
  const [consentError, setConsentError] = useState<string | undefined>();
  const [openFileConfirmed, setOpenFileConfirmed] = useState(false);
  const [openFileConfirmError, setOpenFileConfirmError] = useState<string | undefined>();
  const [submitError, setSubmitError] = useState<string | undefined>();
  const [finishingConsent, setFinishingConsent] = useState(false);
  const [parcelReady, setParcelReady] = useState(!isJourney);

  useEffect(() => {
    if (!isJourney || !sessionId) return;

    let cancelled = false;
    void getSessionParcel(sessionId)
      .then((parcel) => {
        if (cancelled) return;
        setAnswers((prev) => {
          const next = {
            ...prev,
            migrash: parcel.migrash,
            gush: parcel.gush ?? prev.gush,
            helka: parcel.helka ?? prev.helka,
            settlement_name: parcel.settlement_name ?? prev.settlement_name,
            designation: parcel.designation ?? prev.designation,
            area_sqm_registered: parcel.area_sqm_registered ?? prev.area_sqm_registered,
            map_lat: parcel.lat,
            map_lng: parcel.lng,
          };
          void patchSessionSteps(sessionId, {
            current_step: JOURNEY_STEP.INTAKE_VENTURE,
            answers: answersToApi(next),
          }).catch(() => {
            /* best-effort */
          });
          return next;
        });
        setParcelReady(true);
      })
      .catch(() => {
        if (!cancelled) {
          navigate('/map', { replace: true });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [isJourney, sessionId, navigate, setAnswers]);

  const consentAccepted = isConsentChecklistComplete(consentChecked);

  const handleBack = useCallback(() => {
    clearError();
    if (isJourney && currentStep === 0) {
      navigate('/map', { state: { revisiting: true } });
      return;
    }
    setCurrentStep(Math.max(0, currentStep - 1));
  }, [clearError, currentStep, isJourney, navigate, setCurrentStep]);

  const handleNext = useCallback(async () => {
    clearError();

    if (currentStep === 0) {
      const errs = validateVenture(answers, t, { requireMap: !isJourney });
      setVentureErrors(errs);
      if (Object.keys(errs).length > 0) return;
      await saveStep(currentStep, answers, undefined, WIZARD_TO_JOURNEY[0]);
      trackQuestionnaireStepCompleted({
        step_id: 0,
        venture_type: answers.venture_type,
      });
      setCurrentStep(1);
      return;
    }

    if (currentStep === 1) {
      const errs = validateContact(answers, t);
      setContactErrors(errs);
      if (Object.keys(errs).length > 0) return;
      await saveStep(currentStep, answers, undefined, WIZARD_TO_JOURNEY[1]);
      trackQuestionnaireStepCompleted({
        step_id: 1,
        venture_type: answers.venture_type,
      });
      setCurrentStep(2);
      return;
    }

    if (currentStep === 2) {
      await saveStep(currentStep, answers, undefined, WIZARD_TO_JOURNEY[2]);
      trackQuestionnaireStepCompleted({
        step_id: 2,
        venture_type: answers.venture_type,
      });
      setCurrentStep(3);
      return;
    }

    if (currentStep === 3) {
      if (!consentAccepted) {
        setConsentError(t.wizard.validation.consentRequired);
        return;
      }
      if (!openFileConfirmed) {
        setOpenFileConfirmError(t.wizard.consent.submitConfirmRequired);
        return;
      }
      setConsentError(undefined);
      setOpenFileConfirmError(undefined);
      setSubmitError(undefined);
      setFinishingConsent(true);
      try {
        await saveConsent();
        trackQuestionnaireStepCompleted({
          step_id: 3,
          venture_type: answers.venture_type,
        });
        trackConsentAccepted({ consent_version: CONSENT_VERSION });
        if (!sessionId) {
          throw new Error('No session');
        }
        await submitEntrepreneurIntake(sessionId, answers);
        navigate('/assessment', { replace: true });
      } catch (err) {
        const code = err instanceof Error ? err.message : '';
        if (code === 'PROFILE_INCOMPLETE') {
          setSubmitError(t.wizard.consent.submitProfileRequired);
        } else {
          setSubmitError(
            err instanceof Error && code !== 'SUBMIT_FAILED'
              ? err.message
              : t.wizard.consent.submitError,
          );
        }
      } finally {
        setFinishingConsent(false);
      }
    }
  }, [
    answers,
    clearError,
    consentAccepted,
    currentStep,
    navigate,
    openFileConfirmed,
    saveConsent,
    saveStep,
    sessionId,
    setCurrentStep,
    t,
  ]);

  const canProceed =
    currentStep < 3 ||
    (currentStep === 3 && consentAccepted && openFileConfirmed);

  if (isJourney && !parcelReady) {
    return null;
  }

  const stepContent = (() => {
    switch (currentStep) {
      case 0:
        return (
          <VentureDetailsStep
            answers={answers}
            errors={ventureErrors}
            showMapPicker={!isJourney}
            onChange={(patch) => setAnswers((prev) => ({ ...prev, ...patch }))}
          />
        );
      case 1:
        return (
          <ContactStep
            answers={answers}
            errors={contactErrors}
            onChange={(patch) => setAnswers((prev) => ({ ...prev, ...patch }))}
          />
        );
      case 2:
        return <ReviewStep answers={answers} />;
      case 3:
        return (
          <>
            {finishingConsent ? (
              <AppBanner
                variant="info"
                title={t.wizard.finishing}
                text={t.wizard.consent.savingHint}
              />
            ) : null}
            <ConsentStep
              checked={consentChecked}
              error={consentError}
              onChange={(key, value) => {
                setConsentChecked((prev) => ({ ...prev, [key]: value }));
                if (value) setConsentError(undefined);
              }}
              openFileConfirmed={openFileConfirmed}
              onOpenFileConfirmChange={(value) => {
                setOpenFileConfirmed(value);
                if (value) setOpenFileConfirmError(undefined);
              }}
              openFileConfirmError={openFileConfirmError}
              submitError={submitError}
            />
          </>
        );
      default:
        return null;
    }
  })();

  const stepKeys = ['venture', 'contact', 'review', 'consent'] as const;
  const stepKey = stepKeys[currentStep];
  const stepTitle = t.wizard.steps[stepKey];

  return (
    <section
      className={styles.wizard}
      data-testid={isJourney ? 'questionnaire-screen' : 'wizard-screen'}
    >
      <WizardShell currentStep={currentStep} variant={isJourney ? 'journey' : 'standalone'}>
        {error ? (
          <AppBanner variant="danger" title={t.wizard.errorTitle} text={error} />
        ) : null}
        <div className={wizardStyles.card}>
          <header className={wizardStyles.cardHeader}>
            <h2 className={wizardStyles.stepTitle}>{stepTitle}</h2>
            {isJourney && currentStep === 0 ? (
              <p className={wizardStyles.stepLead}>{t.wizard.journeySubtitle}</p>
            ) : !isJourney && currentStep === 0 ? (
              <p className={wizardStyles.stepDesc}>{t.wizard.subtitle}</p>
            ) : null}
          </header>
          <div className={wizardStyles.cardForm}>{stepContent}</div>
          <WizardNav
            currentStep={currentStep}
            isLastStep={currentStep === TOTAL_STEPS - 1}
            canProceed={canProceed}
            loading={loading || finishingConsent}
            showBackOnFirstStep={isJourney}
            nextLabel={
              currentStep === TOTAL_STEPS - 1
                ? finishingConsent
                  ? t.wizard.consent.submitLoading
                  : t.wizard.consent.submit
                : undefined
            }
            onBack={handleBack}
            onNext={() => void handleNext()}
          />
        </div>
      </WizardShell>
    </section>
  );
}
